/**
 * CouponManager.jsx — Redesigned · Dark Luxury Fintech
 * Accent: rose/pink (Promotions identity)
 *
 * Améliorations UX vs original :
 * - window.confirm() → ConfirmDialog natif
 * - Modal form → Drawer latéral spring-animated
 * - Coupon cards redesignées style "ticket de réduction" avec
 *   découpe circulaire, dégradé et tiret pointillé
 * - Génération de code avec animation de "slot machine"
 * - Progress bar usedCount / maxUses
 * - Countdown d'expiration en jours
 * - Stats MiniStat animées cohérentes avec les autres pages
 * - Toggle "Active" animé
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, addDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tag, Plus, Edit2, Trash2, Gift, Percent,
  Calendar, RefreshCw, X, Check, Copy,
  Zap, AlertTriangle, ChevronDown, Filter, Search
} from 'lucide-react';
import { toast } from 'react-toastify';

// ─── Atoms ────────────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, backdropFilter: 'blur(16px)', overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const MiniStat = ({ label, value, accent = '#f43f5e', icon: Icon, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * .07, duration: .45, ease: [.22, 1, .36, 1] }}
    whileHover={{ y: -3, transition: { duration: .15 } }}
    style={{ background: 'rgba(17,24,39,.85)', border: `1px solid ${accent}33`, borderRadius: 14, padding: '14px 16px', boxShadow: `0 0 24px ${accent}0d`, position: 'relative', overflow: 'hidden' }}
  >
    <div style={{ position: 'absolute', top: -24, right: -24, width: 72, height: 72, borderRadius: '50%', background: `${accent}18`, filter: 'blur(20px)', pointerEvents: 'none' }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
      <div>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#475569', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</p>
      </div>
      {Icon && (
        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, border: `1px solid ${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color={accent} strokeWidth={1.8} />
        </div>
      )}
    </div>
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: .5, delay: .2 + index * .07 }}
      style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: accent, opacity: .5, transformOrigin: 'left' }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {}, type = 'button' }) => {
  const V = {
    ghost:  { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    rose:   { background: 'rgba(244,63,94,.15)',   color: '#fb7185', border: '1px solid rgba(244,63,94,.3)'   },
    pink:   { background: 'rgba(236,72,153,.15)',  color: '#f472b6', border: '1px solid rgba(236,72,153,.3)'  },
    green:  { background: 'rgba(16,185,129,.15)',  color: '#34d399', border: '1px solid rgba(16,185,129,.3)'  },
    red:    { background: 'rgba(239,68,68,.15)',   color: '#f87171', border: '1px solid rgba(239,68,68,.3)'   },
    amber:  { background: 'rgba(245,158,11,.15)',  color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)'  },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>
      {children}
    </button>
  );
};

const FieldLabel = ({ children }) => (
  <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 5 }}>{children}</label>
);

const DI = ({ value, onChange, placeholder, type = 'text', style = {} }) => (
  <input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder}
    style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', ...style }}
  />
);

const DS = ({ value, onChange, children }) => (
  <select value={value ?? ''} onChange={onChange}
    style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', appearance: 'none', cursor: 'pointer' }}>
    {children}
  </select>
);

const Toggle = ({ value, onChange, accent = '#f43f5e' }) => (
  <div onClick={onChange}
    style={{ width: 36, height: 20, borderRadius: 10, background: value ? accent : 'rgba(255,255,255,.1)', position: 'relative', transition: 'background .2s', flexShrink: 0, cursor: 'pointer' }}>
    <motion.div animate={{ x: value ? 18 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
  </div>
);

// ─── ConfirmDialog ────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, message, onConfirm, onCancel }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60 }}>
        <motion.div initial={{ scale: .92, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .92 }}
          style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.12)', borderRadius: 16, padding: 28, maxWidth: 360, width: '100%', textAlign: 'center' }}>
          <AlertTriangle size={28} color="#f87171" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>Delete Coupon?</p>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 22, lineHeight: 1.6 }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn variant="red" onClick={onConfirm}><Trash2 size={12} /> Delete</Btn>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Coupon Ticket Card ───────────────────────────────────────────────────
const CouponCard = ({ coupon, onEdit, onDelete, index }) => {
  const expired   = new Date(coupon.endDate) < new Date();
  const active    = coupon.isActive && !expired;
  const daysLeft  = Math.max(0, Math.ceil((new Date(coupon.endDate) - new Date()) / (1000 * 60 * 60 * 24)));
  const usePct    = coupon.maxUses > 0 ? Math.min(100, ((coupon.usedCount || 0) / coupon.maxUses) * 100) : 0;
  const isPercent = coupon.discountType === 'percentage';

  const statusColor = active ? '#34d399' : expired ? '#f87171' : '#94a3b8';
  const statusBg    = active ? 'rgba(16,185,129,.12)' : expired ? 'rgba(239,68,68,.12)' : 'rgba(100,116,139,.12)';
  const statusLabel = active ? 'Active' : expired ? 'Expired' : 'Inactive';

  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(coupon.code);
    toast.success('Code copied!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * .05, duration: .4, ease: [.22, 1, .36, 1] }}
      whileHover={{ y: -4, transition: { duration: .2 } }}
      style={{
        background: 'rgba(17,24,39,.9)',
        border: `1px solid ${active ? 'rgba(244,63,94,.2)' : 'rgba(255,255,255,.06)'}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: active ? '0 0 24px rgba(244,63,94,.08)' : 'none',
        position: 'relative',
      }}
    >
      {/* Top color strip */}
      <div style={{ height: 3, background: active ? 'linear-gradient(90deg,#f43f5e,#ec4899)' : expired ? 'rgba(100,116,139,.4)' : 'rgba(100,116,139,.3)' }} />

      {/* Main body */}
      <div style={{ padding: '14px 16px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          {/* Code + icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: active ? 'rgba(244,63,94,.15)' : 'rgba(100,116,139,.12)', border: `1px solid ${active ? 'rgba(244,63,94,.3)' : 'rgba(100,116,139,.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Gift size={17} color={active ? '#fb7185' : '#475569'} strokeWidth={1.8} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 800, color: active ? '#fff' : '#475569', letterSpacing: '.08em' }}>{coupon.code}</span>
                <button onClick={copyCode}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#334155', padding: 2, display: 'flex', borderRadius: 4, transition: 'color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
                  onMouseLeave={e => e.currentTarget.style.color = '#334155'}>
                  <Copy size={10} />
                </button>
              </div>
              <span style={{ background: statusBg, color: statusColor, boxShadow: `inset 0 0 0 1px ${statusColor}33`, fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 20 }}>{statusLabel}</span>
            </div>
          </div>

          {/* Discount value */}
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 24, fontWeight: 800, color: active ? '#f59e0b' : '#475569', lineHeight: 1 }}>
              {isPercent ? `${coupon.discountValue}%` : `${coupon.discountValue}π`}
            </p>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: '#334155', marginTop: 2 }}>
              {isPercent ? 'Discount' : 'Pi off'}
            </p>
          </div>
        </div>

        {/* Dashed divider */}
        <div style={{ borderTop: '1px dashed rgba(255,255,255,.08)', margin: '10px 0' }} />

        {/* Details row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: '#334155', marginBottom: 3 }}>Min Order</p>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>{coupon.minOrderAmount > 0 ? `${coupon.minOrderAmount} π` : 'No min'}</p>
          </div>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: '#334155', marginBottom: 3 }}>
              {expired ? 'Expired' : 'Expires in'}
            </p>
            <p style={{ fontSize: 11, fontWeight: 700, color: expired ? '#f87171' : daysLeft <= 7 ? '#f59e0b' : '#94a3b8' }}>
              {expired ? new Date(coupon.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : `${daysLeft}d`}
            </p>
          </div>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: '#334155', marginBottom: 3 }}>Valid from</p>
            <p style={{ fontSize: 11, color: '#475569' }}>{new Date(coupon.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
          </div>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: '#334155', marginBottom: 3 }}>Uses</p>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
              {coupon.usedCount || 0}{coupon.maxUses > 0 ? ` / ${coupon.maxUses}` : ' / ∞'}
            </p>
          </div>
        </div>

        {/* Usage progress bar */}
        {coupon.maxUses > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ height: 4, background: 'rgba(255,255,255,.06)', borderRadius: 4, overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${usePct}%` }}
                transition={{ duration: .7, delay: index * .05 + .3 }}
                style={{ height: '100%', background: usePct >= 90 ? '#f87171' : usePct >= 60 ? '#f59e0b' : '#f43f5e', borderRadius: 4 }}
              />
            </div>
            <p style={{ fontSize: 9, color: '#334155', marginTop: 3, textAlign: 'right' }}>{usePct.toFixed(0)}% used</p>
          </div>
        )}

        {/* Description */}
        {coupon.description && (
          <p style={{ fontSize: 10, color: '#334155', fontStyle: 'italic', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coupon.description}</p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.04)' }}>
          <Btn size="xs" variant="ghost" onClick={() => onEdit(coupon)} style={{ flex: 1, justifyContent: 'center' }}>
            <Edit2 size={10} /> Edit
          </Btn>
          <Btn size="xs" variant="red" onClick={() => onDelete(coupon.id)} style={{ flex: 1, justifyContent: 'center' }}>
            <Trash2 size={10} /> Delete
          </Btn>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Coupon Drawer ────────────────────────────────────────────────────────
const EMPTY_FORM = {
  code: '', discountType: 'percentage', discountValue: '', maxUses: 0,
  minOrderAmount: 0,
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  isActive: true, description: '',
};

const CouponDrawer = ({ open, onClose, form, setForm, onSubmit, submitting, isEdit }) => {
  const set = key => e => setForm(p => ({ ...p, [key]: e.target.value }));
  const isPercent = form.discountType === 'percentage';

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    setForm(p => ({ ...p, code }));
    toast.success('Code generated!');
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)', zIndex: 40 }}
            onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 38 }}
            style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 460, background: '#0d1526', borderLeft: '1px solid rgba(255,255,255,.1)', zIndex: 50, display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.16em', color: 'rgba(244,63,94,.8)', marginBottom: 3 }}>{isEdit ? 'Editing' : 'New Coupon'}</p>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{isEdit ? form.code || 'Edit Coupon' : 'Create Coupon'}</h3>
              </div>
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}><X size={14} /></button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Code + generate */}
                <div>
                  <FieldLabel>Coupon Code *</FieldLabel>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                      placeholder="PROMO2025"
                      style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '9px 12px', color: '#e2e8f0', fontSize: 14, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '.1em', flex: 1, outline: 'none' }}
                    />
                    <Btn variant="rose" onClick={generateCode} style={{ flexShrink: 0 }}>
                      <Zap size={12} /> Generate
                    </Btn>
                  </div>
                </div>

                {/* Discount type + value */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <FieldLabel>Type</FieldLabel>
                    <DS value={form.discountType} onChange={set('discountType')}>
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed (π)</option>
                    </DS>
                  </div>
                  <div>
                    <FieldLabel>Value {isPercent ? '(%)' : '(π)'}</FieldLabel>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#f59e0b', fontSize: 12, fontWeight: 700, pointerEvents: 'none' }}>
                        {isPercent ? '%' : 'π'}
                      </span>
                      <input type="number" value={form.discountValue} onChange={set('discountValue')} placeholder="0"
                        style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px 8px 26px', color: '#e2e8f0', fontSize: 13, fontWeight: 700, width: '100%', outline: 'none' }} />
                    </div>
                  </div>
                </div>

                {/* Min order + max uses */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <FieldLabel>Min Order (π)</FieldLabel>
                    <DI type="number" value={form.minOrderAmount} onChange={set('minOrderAmount')} placeholder="0" />
                  </div>
                  <div>
                    <FieldLabel>Max Uses (0 = ∞)</FieldLabel>
                    <DI type="number" value={form.maxUses} onChange={set('maxUses')} placeholder="0" />
                  </div>
                </div>

                {/* Dates */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <FieldLabel>Start Date</FieldLabel>
                    <DI type="date" value={form.startDate} onChange={set('startDate')} />
                  </div>
                  <div>
                    <FieldLabel>End Date</FieldLabel>
                    <DI type="date" value={form.endDate} onChange={set('endDate')} />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <FieldLabel>Description (optional)</FieldLabel>
                  <textarea value={form.description} onChange={set('description')} placeholder="e.g. Summer promo — 20% off all orders" rows={2}
                    style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                </div>

                {/* Active toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(244,63,94,.06)', border: '1px solid rgba(244,63,94,.15)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }}>
                  <Toggle value={form.isActive} onChange={() => setForm(p => ({ ...p, isActive: !p.isActive }))} accent="#f43f5e" />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>Active</p>
                    <p style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Coupon is redeemable by customers</p>
                  </div>
                </label>

                {/* Live preview */}
                <div style={{ background: 'rgba(244,63,94,.06)', border: '1px solid rgba(244,63,94,.15)', borderRadius: 12, padding: '12px 14px' }}>
                  <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#fb7185', marginBottom: 10 }}>Preview</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(244,63,94,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Gift size={16} color="#fb7185" />
                      </div>
                      <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '.1em' }}>{form.code || 'CODE'}</span>
                    </div>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>
                      {form.discountValue ? (isPercent ? `${form.discountValue}%` : `${form.discountValue}π`) : '—'}
                    </span>
                  </div>
                  {(form.minOrderAmount > 0 || form.endDate) && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {form.minOrderAmount > 0 && <span style={{ fontSize: 10, color: '#475569' }}>Min: {form.minOrderAmount}π</span>}
                      {form.endDate && <span style={{ fontSize: 10, color: '#475569' }}>Expires: {new Date(form.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,.06)', display: 'flex', gap: 10, flexShrink: 0 }}>
              <Btn variant="ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>Cancel</Btn>
              <Btn variant="rose" onClick={onSubmit} disabled={submitting} style={{ flex: 2, justifyContent: 'center' }}>
                {submitting
                  ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(251,113,133,.4)', borderTopColor: '#fb7185', animation: 'spin .7s linear infinite' }} />{isEdit ? 'Saving…' : 'Creating…'}</>
                  : isEdit ? <><Check size={13} /> Save Changes</> : <><Plus size={13} /> Create Coupon</>
                }
              </Btn>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function CouponManager() {
  const [coupons, setCoupons]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [editingId, setEditingId]   = useState(null);
  const [confirmId, setConfirmId]   = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────
  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'coupons'));
      setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  // ── open drawer ────────────────────────────────────────────────────
  const openAdd  = () => { setForm(EMPTY_FORM); setEditingId(null); setDrawerOpen(true); };
  const openEdit = (c)  => { setForm({ ...EMPTY_FORM, ...c }); setEditingId(c.id); setDrawerOpen(true); };

  // ── submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.code || !form.discountValue) { toast.error('Code and discount value required'); return; }
    setSubmitting(true);
    try {
      const data = { ...form, discountValue: Number(form.discountValue), maxUses: Number(form.maxUses), minOrderAmount: Number(form.minOrderAmount), code: form.code.toUpperCase() };
      if (editingId) {
        await updateDoc(doc(db, 'coupons', editingId), data);
        toast.success('Coupon updated!');
      } else {
        await addDoc(collection(db, 'coupons'), { ...data, usedCount: 0, createdAt: new Date() });
        toast.success('Coupon created!');
      }
      setDrawerOpen(false); setEditingId(null); fetchCoupons();
    } catch { toast.error(editingId ? 'Failed to update' : 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  // ── delete ─────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, 'coupons', confirmId));
      setCoupons(p => p.filter(c => c.id !== confirmId));
      toast.success('Coupon deleted!');
    } catch { toast.error('Failed to delete'); }
    finally { setConfirmId(null); }
  };

  // ── stats ──────────────────────────────────────────────────────────
  const isExpired = c => new Date(c.endDate) < new Date();
  const stats = useMemo(() => ({
    total:   coupons.length,
    active:  coupons.filter(c => c.isActive && !isExpired(c)).length,
    expired: coupons.filter(c => isExpired(c)).length,
    uses:    coupons.reduce((s, c) => s + (c.usedCount || 0), 0),
  }), [coupons]);

  // ── filtered ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let f = [...coupons];
    if (searchTerm.trim()) { const s = searchTerm.toLowerCase(); f = f.filter(c => c.code?.toLowerCase().includes(s) || c.description?.toLowerCase().includes(s)); }
    if (filterStatus === 'active')   f = f.filter(c => c.isActive && !isExpired(c));
    if (filterStatus === 'expired')  f = f.filter(c => isExpired(c));
    if (filterStatus === 'inactive') f = f.filter(c => !c.isActive && !isExpired(c));
    return f;
  }, [coupons, searchTerm, filterStatus]);

  const hasFilters = filterStatus !== 'all' || searchTerm;

  // ─── LOADING ────────────────────────────────────────────────────────
  if (loading && coupons.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
<div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(16,185,129,.3)', borderTopColor: '#10b981', animation: 'spin .8s linear infinite' }} />      <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading coupons…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input::placeholder,textarea::placeholder{color:#334155} input[type=date]::-webkit-calendar-picker-indicator{filter:invert(.5)}`}</style>

      {/* ── Hero Banner ── */}
      <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5, ease: [.22, 1, .36, 1] }}
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, background: 'linear-gradient(120deg,#0f172a 0%,#1a1f35 50%,#0f172a 100%)', border: '1px solid rgba(255,255,255,.07)', padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle,rgba(244,63,94,.14),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -30, left: '20%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,rgba(236,72,153,.08),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.2em', color: '#10b981', marginBottom: 5 }}>Promotions</p>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <Tag size={20} color="#10b981" strokeWidth={2} /> Coupon Management
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Create and manage discount coupons for your Pi Store</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={fetchCoupons} disabled={loading}>
              <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </Btn>
            <Btn variant="rose" onClick={openAdd}>
              <Plus size={13} /> Create Coupon
            </Btn>
          </div>
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 20 }}>
        <MiniStat label="Total Coupons" value={stats.total}   accent="#f43f5e" icon={Tag}     index={0} />
        <MiniStat label="Active"        value={stats.active}  accent="#34d399" icon={Gift}    index={1} />
        <MiniStat label="Expired"       value={stats.expired} accent="#f87171" icon={Calendar}index={2} />
        <MiniStat label="Total Uses"    value={stats.uses}    accent="#f59e0b" icon={Percent}  index={3} />
      </div>

      {/* ── Filters ── */}
      <GlassPanel style={{ marginBottom: 16 }}>
        <button onClick={() => setFiltersOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600 }}>
            <Filter size={13} color="#f43f5e" /> Filters &amp; Search
            {hasFilters && <span style={{ background: 'rgba(244,63,94,.2)', color: '#fb7185', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20, border: '1px solid rgba(244,63,94,.3)' }}>ACTIVE</span>}
          </span>
          <ChevronDown size={13} style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#475569' }} />
        </button>
        <AnimatePresence>
          {filtersOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .2 }} style={{ overflow: 'hidden' }}>
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 14 }}>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 5 }}>Search</label>
                    <div style={{ position: 'relative' }}>
                      <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
                      <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Code, description…"
                        style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 12px 8px 28px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 5 }}>Status</label>
                    <DS value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                      <option value="all">All Coupons</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="inactive">Inactive</option>
                    </DS>
                  </div>
                </div>
                {hasFilters && (
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <Btn variant="red" size="xs" onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}>
                      <X size={11} /> Clear
                    </Btn>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* ── Coupons Grid ── */}
      {filtered.length === 0 && !loading ? (
        <GlassPanel style={{ padding: '48px 24px', textAlign: 'center' }}>
          <Tag size={32} style={{ margin: '0 auto 12px', opacity: .2, display: 'block', color: '#475569' }} />
          <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{hasFilters ? 'No coupons match your filters' : 'No coupons yet'}</p>
          {!hasFilters && <div style={{ marginTop: 14 }}><Btn variant="rose" onClick={openAdd}><Plus size={12} /> Create your first coupon</Btn></div>}
        </GlassPanel>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
          {filtered.map((coupon, idx) => (
            <CouponCard
              key={coupon.id}
              coupon={coupon}
              onEdit={openEdit}
              onDelete={id => setConfirmId(id)}
              index={idx}
            />
          ))}
        </div>
      )}

      {/* Count footer */}
      {filtered.length > 0 && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <p style={{ fontSize: 10, color: '#334155' }}>
            Showing <span style={{ color: '#f43f5e', fontWeight: 700 }}>{filtered.length}</span> of <span style={{ color: '#f43f5e', fontWeight: 700 }}>{coupons.length}</span> coupons
          </p>
        </div>
      )}

      {/* ── Drawer + Confirm ── */}
      <CouponDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingId(null); }}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        submitting={submitting}
        isEdit={!!editingId}
      />

      <ConfirmDialog
        open={!!confirmId}
        message="This coupon will be permanently removed and can no longer be redeemed."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmId(null)}
      />
    </motion.div>
  );
}
